/**
 * AdminBroadcast — admin composer page for pushing announcements to the
 * whole community (or a sub-segment) via the notifications system.
 *
 * Flow:
 *   1. Compose title / body / optional link + icon
 *   2. Pick a target segment (all, admin, pro, free, active)
 *   3. Live preview of what recipients will see in their bell popover
 *   4. Send -> POST /api/admin/notifications/broadcast
 *   5. History log at the bottom (last 30 broadcasts) with read-rate badge
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import {
  Megaphone, Send, Users, ShieldCheck, Award, User as UserIcon,
  Activity, ArrowLeft, MessageSquare, BookOpen, Beaker, Info,
  CheckCircle2, Loader2, Bell,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import ThemeToggle from '../../components/ThemeToggle';
import ToolSelector from '../../components/ToolSelector';
import BrandMark from '../../components/BrandMark';
import PageHero from '../../components/PageHero';
import { sendTestPush, enableWebPush, isPushSupported } from '../../lib/webpush';

const TARGETS = [
  { key: 'all',    label: 'Everyone',        icon: Users,        note: 'All members regardless of tier' },
  { key: 'pro',    label: 'Pro members',     icon: Award,        note: 'Anyone on the Pro tier' },
  { key: 'free',   label: 'Free members',    icon: UserIcon,     note: 'Free-tier users only' },
  { key: 'admin',  label: 'Admins only',     icon: ShieldCheck,  note: 'Internal team broadcast' },
  { key: 'active', label: 'Active members',  icon: Activity,     note: 'Users with status = active' },
];

const ICONS = [
  { key: 'megaphone',      icon: Megaphone,     label: 'Announcement' },
  { key: 'message-square', icon: MessageSquare, label: 'Community' },
  { key: 'book-open',      icon: BookOpen,      label: 'New content' },
  { key: 'beaker',         icon: Beaker,        label: 'Product update' },
  { key: 'info',           icon: Info,          label: 'Info' },
];

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleString();
};

const AdminBroadcast = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [icon, setIcon] = useState('megaphone');
  const [target, setTarget] = useState('all');
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/notifications/broadcast-history`);
      setHistory(data?.items || []);
    } catch (_) { /* ignore */ }
    setHistoryLoading(false);
  }, [API]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const send = async () => {
    if (!title.trim()) { toast.error('Add a title first'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/notifications/broadcast`, {
        title: title.trim(),
        body: body.trim(),
        link: link.trim() || null,
        icon,
        target,
      });
      toast.success(`Broadcast sent to ${data?.sent ?? 0} member${data?.sent === 1 ? '' : 's'}`);
      setTitle(''); setBody(''); setLink('');
      await fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    }
    setSending(false);
  };

  // Preview-to-self: sends the exact composed message ONLY to the admin's own
  // notification inbox + push devices so they can eyeball the real thing
  // before firing at the whole segment.
  const sendTestToMe = async () => {
    if (!title.trim()) { toast.error('Add a title first'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/notifications/broadcast`, {
        title: title.trim(),
        body: body.trim(),
        link: link.trim() || null,
        icon,
        target: 'admin',
      });
      // Also try to fire a push to whoever's admin has this device subscribed.
      if (isPushSupported()) {
        try { await sendTestPush(API); } catch (_) { /* ignore */ }
      }
      toast.success(`Test sent to ${data?.sent ?? 0} admin${data?.sent === 1 ? '' : 's'} · check your bell + device`);
      await fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    }
    setTesting(false);
  };

  const enablePushForMe = async () => {
    const r = await enableWebPush(API);
    if (r.ok) toast.success('Push enabled — try Send test to me');
    else if (r.reason === 'permission_denied') toast.error('Permission denied. Open site settings.');
    else toast.error('Could not enable push');
  };

  const IconPreview = (ICONS.find((i) => i.key === icon) || ICONS[0]).icon;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-broadcast-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/portal')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/80 hover:text-foreground"
            data-testid="broadcast-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </button>
          <BrandMark size="md" onClick={() => navigate('/dashboard')} />
          <ThemeToggle />
        </div>
      </header>

      <ToolSelector />

      <PageHero
        eyebrow="Admin"
        eyebrowIcon={Megaphone}
        title={<>Send an <span className="text-amber-brand">announcement</span></>}
        description="Compose a message, pick a target segment, and push it to every recipient's notification inbox. Web-push subscribers will get a device notification too (once they opt in)."
        backTo="/admin/portal"
        backLabel="Admin"
        testId="broadcast-hero"
      />

      <main className="container mx-auto px-6 py-8 max-w-6xl grid lg:grid-cols-[1fr_380px] gap-8">
        {/* ── Composer ──────────────────────────────────────── */}
        <div className="space-y-6">
          <section className="pro-card p-6 space-y-5" data-testid="broadcast-composer">
            <div>
              <span className="eyebrow">Message</span>
              <h2 className="font-heading text-xl font-bold tracking-tight mt-1">Compose broadcast</h2>
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-title">Title <span className="text-destructive">*</span></Label>
              <Input
                id="broadcast-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. New advanced cycles course is live"
                maxLength={100}
                data-testid="broadcast-title-input"
              />
              <div className="text-[11px] text-muted-foreground text-right">{title.length}/100</div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-body">Body</Label>
              <Textarea
                id="broadcast-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Short context — 1-2 sentences work best."
                rows={4}
                maxLength={400}
                data-testid="broadcast-body-input"
              />
              <div className="text-[11px] text-muted-foreground text-right">{body.length}/400</div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-link">Deep link (optional)</Label>
              <Input
                id="broadcast-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="e.g. /course/advanced-cycles"
                data-testid="broadcast-link-input"
              />
              <div className="text-[11px] text-muted-foreground">
                In-app path recipients will land on when they tap the notification.
              </div>
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2" data-testid="broadcast-icon-picker">
                {ICONS.map(({ key, icon: Ic, label }) => {
                  const active = icon === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setIcon(key)}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground/80 hover:border-primary/60'}`}
                      data-testid={`broadcast-icon-${key}`}
                    >
                      <Ic className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="pro-card p-6 space-y-5" data-testid="broadcast-target">
            <div>
              <span className="eyebrow">Target</span>
              <h2 className="font-heading text-xl font-bold tracking-tight mt-1">Who receives this?</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {TARGETS.map(({ key, label, icon: Ic, note }) => {
                const active = target === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTarget(key)}
                    className={`text-left rounded-xl border p-4 transition-colors flex items-start gap-3 ${active ? 'border-primary bg-primary/[.06]' : 'border-border bg-card hover:border-primary/60'}`}
                    data-testid={`broadcast-target-${key}`}
                    aria-pressed={active}
                  >
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      <Ic className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-extrabold text-sm">{label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{note}</div>
                    </div>
                    {active && <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-end">
              <Button
                onClick={send}
                disabled={sending || !title.trim()}
                className="rounded-full font-semibold px-5"
                data-testid="broadcast-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {sending ? 'Sending…' : 'Send broadcast'}
              </Button>
            </div>
          </section>
        </div>

        {/* ── Live preview + history ───────────────────────── */}
        <aside className="space-y-6">
          <section className="pro-card p-5" data-testid="broadcast-preview">
            <span className="eyebrow">Live preview</span>
            <div className="text-xs text-muted-foreground mt-1 mb-4">This is what recipients see in their bell.</div>
            <div className="rounded-xl border border-border bg-background p-4 flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                <IconPreview className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-foreground truncate flex items-center gap-2">
                  {title || 'Your title here'}
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                </div>
                {body ? (
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{body}</div>
                ) : (
                  <div className="text-xs text-muted-foreground italic mt-0.5">Body appears here…</div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground/80 mt-1">just now</div>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              Recipients get an unread badge on their bell instantly. Web-push subscribers will also get a device pop-up
              once they&apos;ve granted permission in their profile.
            </div>
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-2">
              <Button
                onClick={sendTestToMe}
                disabled={testing || !title.trim()}
                variant="outline"
                size="sm"
                className="rounded-full font-semibold"
                data-testid="broadcast-test-to-me-btn"
              >
                {testing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Bell className="w-3.5 h-3.5 mr-2" />}
                Send test to me
              </Button>
              {isPushSupported() && (
                <Button
                  onClick={enablePushForMe}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-xs font-semibold text-muted-foreground"
                  data-testid="broadcast-enable-push-btn"
                >
                  Enable push on this device
                </Button>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              Fires an admin-only broadcast + push to whoever is signed in — a safety net before hitting Send-to-All.
            </div>
          </section>

          <section className="pro-card p-5" data-testid="broadcast-history">
            <div className="flex items-center justify-between">
              <span className="eyebrow">History</span>
              <span className="text-[11px] text-muted-foreground">{history.length} recent</span>
            </div>
            <div className="mt-3 space-y-3 max-h-[560px] overflow-y-auto">
              {historyLoading && (
                <div className="text-center py-6 text-xs text-muted-foreground">Loading…</div>
              )}
              {!historyLoading && history.length === 0 && (
                <div className="text-center py-6 text-xs text-muted-foreground">No broadcasts yet.</div>
              )}
              {!historyLoading && history.map((h, i) => {
                const readRate = h.sent ? Math.round((h.read / h.sent) * 100) : 0;
                return (
                  <div
                    key={i}
                    className="rounded-lg border border-border bg-background p-3"
                    data-testid={`broadcast-history-item-${i}`}
                  >
                    <div className="text-sm font-bold truncate">{h.title}</div>
                    {h.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{h.body}</div>}
                    <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground/80">
                      <span>{h.target || 'all'} · {relTime(h.created_at)}</span>
                      <span className="text-foreground/80">
                        {h.sent} sent · <span className="text-primary font-bold">{readRate}% read</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
};

export default AdminBroadcast;
